import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { cleanCurrentCourse } from '@src/store/actions/course';
import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { STATE_FAIL, STATE_SUCCESS, STATE_LOADING } from '@src/const/common';
import NoCoursePage from '@src/pages/noCoursePage';
import CourseNavigation from '@src/components/course/advancedInfo/courseNavigation';
import CustomSeo from '@src/components/seo/customSeo';
import Loading from '@src/components/Loading';
import { isHardcodeUseCourseCustomerHTML } from '@src/utils/hardcode';

import BasicInfo from './basicInfo';
import AdvancedInfo from './advancedInfo';
import './global.module.less';

const CoursePageBackground = styled.div`
	background: #FFFFFF;
`;

const WidthLimitContainer = styled.div`
	padding: 0;
	background: #FFFFFF;

	@media ${props => props.theme.device.lgDesktop} {
		width: 1200px;
		margin: 0 auto;
	}
`;

const CustomerHTMLContainer = styled.div`
	margin-top: 8px;
	@media 
	${props => props.theme.device.mobile},
	${props => props.theme.device.smTablet} {
		margin-top: 60px;
	}
`;

const CoursePage = (): JSX.Element => {
	const dispatch = useDispatch();
	const router = useRouter();
	const { courseId } = router.query;
	const customerHTML = isHardcodeUseCourseCustomerHTML(courseId);
	const { currentCourse, currentCourseGeneralState, currentCourseState, isPreview } = useCurrentCourse();
	let isCourseLoaded = currentCourseGeneralState === STATE_SUCCESS;
	let isCourseDetailLoaded = currentCourseState === STATE_SUCCESS;
	let isCourseLoading = currentCourseGeneralState === STATE_LOADING;
	if (isPreview) {
		isCourseLoaded = currentCourseState === STATE_SUCCESS;
		isCourseDetailLoaded = currentCourseState === STATE_SUCCESS;
		isCourseLoading = currentCourseState === STATE_LOADING;
	}

	// Bugfix for TEACHES-1918 temporarily.
	useEffect(() => () => {
		dispatch(cleanCurrentCourse());
	}, []);

	if (!courseId) {
		// Hardcode to fix url with wrong course id format
		return null;
	}

	if (isCourseLoading) {
		return <Loading />;
	}

	if (currentCourseGeneralState === STATE_FAIL) {
		return <NoCoursePage />;
	}

	const seoSetting = {
		seoTitle: currentCourse?.seo_title || currentCourse?.seo?.seo_title || '',
		seoDescription: currentCourse?.seo_description || currentCourse?.seo?.seo_description || '',
		seoKeywords: currentCourse?.seo_keywords || currentCourse?.seo?.seo_keywords || '',
		seoImage: currentCourse?.cover_link?.link || '',
	};

	return isCourseLoaded && currentCourse?.id && (
		<CoursePageBackground>
			<CustomSeo seoSetting={seoSetting} />
			{
				customerHTML && (
					<CustomerHTMLContainer
						dangerouslySetInnerHTML={{
							__html: customerHTML,
						}}
					/>
				)
			}
			<WidthLimitContainer>
				<BasicInfo hasCustomHTML={!!customerHTML} />
			</WidthLimitContainer>
			<CourseNavigation />
			<WidthLimitContainer>
				{isCourseDetailLoaded && <AdvancedInfo />}
			</WidthLimitContainer>
		</CoursePageBackground>
	);
};

export default CoursePage;
